import { Injectable } from '@angular/core';
import { RequestedResourceRow } from '@core/models/requested-resource-row.model';
import { HttpQueryBuilder } from '@portal-shared/app/builders/http-query.builder';
import { Classification } from '@portal-shared/app/entities/class.model';
import { SortDirectionEnum } from '@portal-shared/app/enums/sort-directions.enum';
import { TimerUtils } from '@portal-shared/app/utils/timer.utils';
import { AllyaStorageService } from '@services/allya-storage.service';
import { AllyaRouterHelper } from '@shared/helpers/router.helper';
import { BehaviorSubject, debounceTime, skip } from 'rxjs';
import { AssignmentBoardFilterUpdate } from '../models/assignment-board-filter-update.model';
import { AssignmentBoardFilter } from '../models/assignment-board-filter.model';

@Injectable({
  providedIn: 'root'
})
export class AssignmentBoardFiltersService {
  public isFiltersOpen = true;

  private filterName = 'AssignmentBoardFilters';

  private filters$ = new BehaviorSubject<AssignmentBoardFilterUpdate>({
    key: undefined,
    filters: this.getDefaultFilters()
  });

  public enablePersistence = false;

  public constructor(
    private allyaStorageService: AllyaStorageService,
    private allyaRouterHelper: AllyaRouterHelper
  ) {}

  public init() {
    return this.allyaRouterHelper.get(AllyaRouterHelper.requestIdParam).subscribe(request => {
      this.enablePersistence = !request;

      const filters = this.allyaStorageService.getItem<AssignmentBoardFilter>(this.filterName);

      if (filters) {
        this.updateFiltersQuietly(filters);
      } else {
        this.clearFilters();
      }
    });
  }

  private getDefaultFilters(): AssignmentBoardFilter {
    return {
      sortDirection: SortDirectionEnum.Asc,
      orderBy: 'ResourceName',
      classes: [],
      departments: []
    };
  }

  public getFilters() {
    return this.filters$.getValue().filters;
  }

  public getHiddenActiveFiltersCount() {
    const filter = this.getFilters();
    const defaultFilter = this.getDefaultFilters();

    let count = 0;
    if (filter.classes.length) {
      count++;
    }
    if (filter.departments.length) {
      count++;
    }
    if (filter.orderBy !== defaultFilter.orderBy || filter.sortDirection !== defaultFilter.sortDirection) {
      count++;
    }
    if (filter.hideResourcesWithConflicts) {
      count++;
    }

    return `${count}`;
  }

  public clearFilters(options?: { keepSearch?: boolean }) {
    const filters = this.getFilters();

    const newFilter = this.getDefaultFilters();

    if (options?.keepSearch) {
      newFilter.searchKeys = filters.searchKeys;
    }

    this.filters$.next({
      filters: newFilter
    });
  }

  public updateFilter<TKey extends keyof AssignmentBoardFilter>(key: TKey, value: AssignmentBoardFilter[TKey]) {
    const filters = this.getFilters();
    filters[key] = value;
    this.filters$.next({
      key,
      filters
    });
    setTimeout(() => {
      if (this.enablePersistence) {
        this.allyaStorageService.setItem(this.filterName, filters);
      }
    }, 1000);

    return filters;
  }

  public updateFiltersQuietly(value: AssignmentBoardFilter) {
    this.filters$.getValue().filters = value;
  }

  public observeChanges() {
    return this.filters$.asObservable().pipe(
      skip(1), // behaviour subject only get the changes not the inital state
      debounceTime(TimerUtils.filtersDebounceMs)
    );
  }

  public apply(query: HttpQueryBuilder) {
    const filters = this.getFilters();

    if (filters.searchKeys) {
      query.search(filters.searchKeys.join(','));
    }

    if (filters.departments.length) {
      query.filterBy(
        'departmentIds',
        filters.departments.map(c => c.id)
      );
    }

    if (filters.classes.length) {
      query.filterBy(
        'classIds',
        filters.classes.map(c => c.id)
      );
    }

    if (filters.hideResourcesWithConflicts) {
      query.queryParam('hideResourcesWithConflicts', 'true');
    }

    const orderBy = filters.orderBy;
    query.sort([{ colId: orderBy, sort: filters.sortDirection }]);

    return query;
  }

  public setFiltersForRequest(request: RequestedResourceRow) {
    this.enablePersistence = false;
    const filters = this.getFilters();

    if (Classification.isClassAll(request.classification.id)) {
      filters.classes = [];
    } else {
      filters.classes = [
        {
          description: request.classification.label,
          id: request.classification.id,
          isSelected: true
        }
      ];
    }
    filters.departments = [
      {
        description: request.department.label,
        id: request.department.id,
        isSelected: true
      }
    ];

    return filters;
  }

  public isFilteredByRequest(request: RequestedResourceRow) {
    const filters = this.getFilters();

    if (Classification.isClassAll(request.classification.id)) {
      if (filters.classes.length) {
        return false;
      }
    } else {
      if (filters.classes.length != 1 || !filters.classes.find(d => d.id === request.classification.id)) {
        return false;
      }
    }

    if (filters.departments.length != 1 || !filters.departments.find(d => d.id === request.department.id)) {
      return false;
    }

    return true;
  }
}
